import { supabase } from '@/lib/supabase/client';

export interface Appointment {
  id: string;
  title: string;
  description: string | null;
  type: 'meeting' | 'notary' | 'client_visit';
  clientId: string | null;
  assignedTo: string | null;
  startTime: string;
  endTime: string;
  location: string | null;
  notes: string | null;
  isCompleted: boolean;
  status: 'scheduled' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled' | 'no_show';
  reminderSent: boolean;
  reminderTime: string | null;
  cancelledReason: string | null;
  cancelledAt: string | null;
  cancelledBy: string | null;
  createdAt: string;
  updatedAt: string;
  clientName?: string;
  clientEmail?: string;
}

export async function getAllAppointments(): Promise<Appointment[]> {
  try {
    const { data, error } = await supabase
      .from('appointments')
      .select(`
        *,
        clients!appointments_client_id_fkey(
          id,
          user_profiles!clients_user_id_fkey(
            full_name,
            email
          )
        )
      `)
      .order('start_time', { ascending: true });

    if (error) throw error;

    return (data || []).map(row => ({
      id: row.id,
      title: row.title,
      description: row.description,
      type: row.type,
      clientId: row.client_id,
      assignedTo: row.assigned_to,
      startTime: row.start_time,
      endTime: row.end_time,
      location: row.location,
      notes: row.notes,
      isCompleted: row.is_completed,
      status: row.status || 'scheduled',
      reminderSent: row.reminder_sent || false,
      reminderTime: row.reminder_time,
      cancelledReason: row.cancelled_reason,
      cancelledAt: row.cancelled_at,
      cancelledBy: row.cancelled_by,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
      clientName: row.clients?.user_profiles?.full_name || null,
      clientEmail: row.clients?.user_profiles?.email || null
    }));
  } catch (error) {
    console.error('Error fetching appointments:', error);
    return [];
  }
}

export async function createAppointment(appointment: Partial<Appointment>): Promise<boolean> {
  try {
    // Calculate reminder time (24 hours before start time)
    let reminderTime = null;
    if (appointment.startTime) {
      const startDate = new Date(appointment.startTime);
      reminderTime = new Date(startDate.getTime() - 24 * 60 * 60 * 1000).toISOString();
    }

    const { error } = await supabase
      .from('appointments')
      .insert({
        title: appointment.title,
        description: appointment.description,
        type: appointment.type,
        client_id: appointment.clientId,
        assigned_to: appointment.assignedTo,
        start_time: appointment.startTime,
        end_time: appointment.endTime,
        location: appointment.location,
        notes: appointment.notes,
        status: appointment.status || 'scheduled',
        reminder_time: reminderTime
      });

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error creating appointment:', error);
    return false;
  }
}

export async function deleteAppointment(id: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('appointments')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting appointment:', error);
    return false;
  }
}

export async function updateAppointment(id: string, updates: Partial<Appointment>): Promise<boolean> {
  try {
    const updateData: any = {};
    
    if (updates.title !== undefined) updateData.title = updates.title;
    if (updates.description !== undefined) updateData.description = updates.description;
    if (updates.type !== undefined) updateData.type = updates.type;
    if (updates.clientId !== undefined) updateData.client_id = updates.clientId;
    if (updates.assignedTo !== undefined) updateData.assigned_to = updates.assignedTo;
    if (updates.startTime !== undefined) updateData.start_time = updates.startTime;
    if (updates.endTime !== undefined) updateData.end_time = updates.endTime;
    if (updates.location !== undefined) updateData.location = updates.location;
    if (updates.notes !== undefined) updateData.notes = updates.notes;
    if (updates.status !== undefined) updateData.status = updates.status;
    if (updates.isCompleted !== undefined) updateData.is_completed = updates.isCompleted;
    if (updates.cancelledReason !== undefined) updateData.cancelled_reason = updates.cancelledReason;
    if (updates.cancelledBy !== undefined) updateData.cancelled_by = updates.cancelledBy;
    if (updates.status === 'cancelled' && !updates.cancelledAt) {
      updateData.cancelled_at = new Date().toISOString();
    }

    const { error } = await supabase
      .from('appointments')
      .update(updateData)
      .eq('id', id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error updating appointment:', error);
    return false;
  }
}